import {
  decodeFunctionData,
  formatEther,
  formatUnits,
  isHex,
  multicall3Abi,
  zeroAddress,
} from 'viem';
import type { Address, Chain, Hash, PublicClient, Transport } from 'viem';

import { logger } from '@/lib/logger';

import { hyperEvm } from '@/constants/chain';

import {
  ALLOWANCE_HOLDER_ADDRESS,
  ALLOWANCE_HOLDER_EXEC_ABI,
  ERC_4337_ENTRY_POINT,
  FORWARDING_MULTICALL_ABI,
  FORWARDING_MULTICALL_ADDRESS,
  FUNCTION_SELECTORS,
  MULTICALL3_ADDRESS,
  NATIVE_SYMBOL_BY_CHAIN_ID,
  NATIVE_TOKEN_ADDRESS,
  PARSE_SUPPORTED_CHAINS,
  SETTLER_EXECUTE_ABI,
  SETTLER_META_TXN_ABI,
} from './constants';
import type { ParsedSwap, Trace, TraceTransactionSchema } from './types';
import {
  calculateNativeTransfer,
  isChainIdSupported,
  parseSmartContractWalletTx,
  transferLogs,
} from './utils';

export type { ParsedSwap, ParsedSwapToken } from './types';
export { isChainIdSupported } from './utils';

function findNestedAllowanceHolderSettlerExec(trace: Trace): Trace | undefined {
  if (
    trace.to?.toLowerCase() === ALLOWANCE_HOLDER_ADDRESS.toLowerCase() &&
    trace.input.startsWith(FUNCTION_SELECTORS.ALLOWANCE_HOLDER_EXEC)
  ) {
    const { args } = decodeFunctionData({
      abi: ALLOWANCE_HOLDER_EXEC_ABI,
      data: trace.input,
    });
    const settlerCallData = args[4];

    if (settlerCallData.startsWith(FUNCTION_SELECTORS.SETTLER_EXECUTE)) {
      return trace;
    }
  }

  for (const call of trace.calls ?? []) {
    const allowanceHolderExec = findNestedAllowanceHolderSettlerExec(call);
    if (allowanceHolderExec) return allowanceHolderExec;
  }

  return undefined;
}

export async function parseSwap({
  publicClient,
  transactionHash: hash,
  smartContractWallet,
}: {
  publicClient: PublicClient<Transport, Chain>;
  transactionHash: Hash;
  smartContractWallet?: Address;
}): Promise<ParsedSwap | null> {
  const chainId = publicClient.chain.id;

  if (!isChainIdSupported(chainId)) {
    throw new Error(`chainId ${chainId} is unsupported.`);
  }

  const client = publicClient.extend((client) => ({
    async traceCall(args: { hash: Hash }) {
      return client.request<TraceTransactionSchema>({
        method: 'debug_traceTransaction',
        params: [args.hash, { tracer: 'callTracer' }],
      });
    },
  }));

  const [trace, transaction, transactionReceipt] = await Promise.all([
    client.traceCall({ hash }),
    publicClient.getTransaction({ hash }),
    publicClient.getTransactionReceipt({ hash }),
  ]);

  const { from: taker, value, to } = transaction;

  const isToERC4337 = to?.toLowerCase() === ERC_4337_ENTRY_POINT.toLowerCase();

  const nativeAmountToTaker = calculateNativeTransfer(trace, {
    recipient: taker,
  });

  if (transactionReceipt.status === 'reverted') {
    const chain = PARSE_SUPPORTED_CHAINS.find((chain) => chain.id === chainId);
    const message = `Unable to parse. Transaction ${hash} on ${chain?.name} has reverted.`;
    logger.warn(message);
    return null;
  }

  const isNativeSell = value > 0n;

  const logs = await transferLogs({
    publicClient,
    transactionReceipt,
  });

  if (chainId === hyperEvm.id) {
    const allowanceHolderExec = findNestedAllowanceHolderSettlerExec(trace);

    if (allowanceHolderExec) {
      const { args: allowanceHolderArgs } = decodeFunctionData({
        abi: ALLOWANCE_HOLDER_EXEC_ABI,
        data: allowanceHolderExec.input,
      });
      const [, sellToken, , settler, settlerCallData] = allowanceHolderArgs;
      const { args: settlerArgs } = decodeFunctionData({
        abi: SETTLER_EXECUTE_ABI,
        data: settlerCallData,
      });
      const { recipient, buyToken } = settlerArgs[0];

      const input =
        sellToken.toLowerCase() === zeroAddress
          ? {
              symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
              amount: formatEther(BigInt(allowanceHolderExec.value)),
              address: NATIVE_TOKEN_ADDRESS,
            }
          : logs
              .filter(
                (log) =>
                  log.address.toLowerCase() === sellToken.toLowerCase() &&
                  log.from.toLowerCase() === allowanceHolderExec.from.toLowerCase() &&
                  log.to.toLowerCase() === settler.toLowerCase(),
              )
              .reduce(
                (acc, curr) => ({
                  symbol: curr.symbol,
                  amount: formatUnits(acc.amountRaw + curr.amountRaw, curr.decimals),
                  amountRaw: acc.amountRaw + curr.amountRaw,
                  address: curr.address,
                }),
                { symbol: '', amount: '', amountRaw: 0n, address: '' },
              );

      const output =
        buyToken.toLowerCase() === NATIVE_TOKEN_ADDRESS.toLowerCase()
          ? {
              symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
              amount: calculateNativeTransfer(trace, { recipient }),
              address: NATIVE_TOKEN_ADDRESS,
            }
          : logs
              .filter(
                (log) =>
                  log.address.toLowerCase() === buyToken.toLowerCase() &&
                  log.to.toLowerCase() === recipient.toLowerCase(),
              )
              .reduce(
                (acc, curr) => ({
                  symbol: curr.symbol,
                  amount: formatUnits(acc.amountRaw + curr.amountRaw, curr.decimals),
                  amountRaw: acc.amountRaw + curr.amountRaw,
                  address: curr.address,
                }),
                { symbol: '', amount: '', amountRaw: 0n, address: '' },
              );

      if (!input.address || !output.address || input.amount === '0' || output.amount === '0') {
        return null;
      }

      return {
        tokenIn: {
          symbol: input.symbol,
          amount: input.amount,
          address: input.address,
        },
        tokenOut: {
          symbol: output.symbol,
          amount: output.amount,
          address: output.address,
        },
      };
    }
  }

  if (isToERC4337) {
    if (!smartContractWallet) {
      throw new Error(
        'This is an ERC-4337 transaction. You must provide a smart contract wallet address to 0x-parser.',
      );
    }

    return parseSmartContractWalletTx({
      logs,
      trace,
      chainId,
      smartContractWallet,
    });
  }

  const fromTaker = logs.filter((log) => log.from.toLowerCase() === taker.toLowerCase());

  let input = fromTaker.length
    ? fromTaker.reduce((acc, curr) => ({
        ...acc,
        amount: formatUnits(acc.amountRaw + curr.amountRaw, curr.decimals),
        amountRaw: acc.amountRaw + curr.amountRaw,
      }))
    : logs[0];

  let output =
    nativeAmountToTaker === '0'
      ? logs.find((log) => {
          return log.to.toLowerCase() === taker.toLowerCase();
        })
      : {
          symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
          amount: nativeAmountToTaker,
          address: NATIVE_TOKEN_ADDRESS,
        };
  if (to?.toLowerCase() === FORWARDING_MULTICALL_ADDRESS.toLowerCase()) {
    const { args: multicallArgs } = decodeFunctionData({
      abi: FORWARDING_MULTICALL_ABI,
      data: transaction.input,
    });

    const settlerCallData =
      Array.isArray(multicallArgs) && Array.isArray(multicallArgs[0])
        ? multicallArgs[0][1]?.data
        : undefined;

    if (isHex(settlerCallData)) {
      const { args: settlerArgs } = decodeFunctionData({
        abi: SETTLER_META_TXN_ABI,
        data: settlerCallData,
      });

      const recipient = settlerArgs[0].recipient.toLowerCase() as Address;

      const msgSender = settlerArgs[3];

      const nativeAmountToTaker = calculateNativeTransfer(trace, {
        recipient,
      });

      if (nativeAmountToTaker === '0') {
        [output] = logs.filter((log) => log.to.toLowerCase() === msgSender.toLowerCase());
      } else {
        output = {
          symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
          amount: nativeAmountToTaker,
          address: NATIVE_TOKEN_ADDRESS,
        };
      }
    }
  }
  if (to?.toLowerCase() === MULTICALL3_ADDRESS.toLowerCase()) {
    const { args: multicallArgs } = decodeFunctionData({
      abi: multicall3Abi,
      data: transaction.input,
    });

    const settlerCallData =
      Array.isArray(multicallArgs) && Array.isArray(multicallArgs[0])
        ? multicallArgs[0][1]?.callData
        : undefined;

    if (isHex(settlerCallData)) {
      const { args: settlerArgs } = decodeFunctionData({
        abi: SETTLER_META_TXN_ABI,
        data: settlerCallData,
      });

      const takerForGaslessApprovalSwap = settlerArgs[0].recipient.toLowerCase() as Address;

      const msgSender = settlerArgs[3];

      const nativeAmountToTaker = calculateNativeTransfer(trace, {
        recipient: takerForGaslessApprovalSwap,
      });

      if (nativeAmountToTaker === '0') {
        [output] = logs.filter((log) => log.to.toLowerCase() === msgSender.toLowerCase());
      } else {
        output = {
          symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
          amount: nativeAmountToTaker,
          address: NATIVE_TOKEN_ADDRESS,
        };
      }
    }
  }

  if (transaction.input.startsWith(FUNCTION_SELECTORS.EXECUTE_META_TXN)) {
    const { args } = decodeFunctionData({
      abi: SETTLER_META_TXN_ABI,
      data: transaction.input,
    });

    const { 3: msgSender } = args;

    const logsFromTaker = logs.filter((log) => log.from.toLowerCase() === msgSender.toLowerCase());

    const nativeAmountToTaker = calculateNativeTransfer(trace, {
      recipient: msgSender,
    });

    if (logsFromTaker.length) {
      input = logsFromTaker[0];
    }

    if (nativeAmountToTaker === '0') {
      output = logs[logs.length - 1];
      const takerReceived = logs.filter((log) => log.to.toLowerCase() === msgSender.toLowerCase());
      if (takerReceived.length === 1) {
        output = {
          symbol: takerReceived[0].symbol,
          amount: takerReceived[0].amount,
          address: takerReceived[0].address,
        };
      } /* v8 ignore start */ else {
        // Unknown if this case actually happens. If it does, please file a bug report here: https://github.com/0xProject/0x-parser/issues/new/choose".
        logger.error(
          'File a bug report here, including the expected results (URL to a block explorer) and the unexpected results: https://github.com/0xProject/0x-parser/issues/new/choose.',
        );
      }

      input = logs.filter((log) => log.from.toLowerCase() === msgSender.toLowerCase())[0];

      /* v8 ignore stop */
    } else {
      output = {
        symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
        amount: nativeAmountToTaker,
        address: NATIVE_TOKEN_ADDRESS,
      };
    }
  }

  if (isNativeSell) {
    const nativeSellAmount = formatEther(value);
    const tokenOut = logs
      .filter((log) => log.to.toLowerCase() === taker.toLowerCase())
      .reduce(
        (acc, curr) => ({
          symbol: curr.symbol,
          amount: formatUnits(acc.amountRaw + curr.amountRaw, curr.decimals),
          amountRaw: acc.amountRaw + curr.amountRaw,
          address: curr.address,
        }),
        { symbol: '', amount: '', amountRaw: 0n, address: '' },
      );

    return {
      tokenIn: {
        symbol: NATIVE_SYMBOL_BY_CHAIN_ID[chainId],
        address: NATIVE_TOKEN_ADDRESS,
        amount: nativeSellAmount,
      },
      tokenOut: {
        symbol: tokenOut.symbol,
        amount: tokenOut.amount,
        address: tokenOut.address,
      },
    };
  }

  /* v8 ignore start */
  if (!input || !output) {
    logger.error(
      'File a bug report here, including the expected results (URL to a block explorer) and the unexpected results: https://github.com/0xProject/0x-parser/issues/new/choose.',
    );
    return null;
  }
  /* v8 ignore stop */

  return {
    tokenIn: {
      symbol: input.symbol,
      amount: input.amount,
      address: input.address,
    },
    tokenOut: {
      symbol: output.symbol,
      amount: output.amount,
      address: output.address,
    },
  };
}
